// src/utils/assignDriverToVehicle.js
import { db } from "../firebase/firebase";
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";

/**
 * Modes supported:
 *  - "assign" (first assignment)
 *  - "reassign" (oldEndDate + newStartDate)
 *  - "unassign" (only endDate)
 */
export async function assignDriverToVehicle({
  mode = "assign",
  driverId,
  driverName,
  vehicleId,
  vehicleNumber,
  company,
  vehicleType,
  startDate,     // for first assignment
  oldEndDate,    // for reassignment
  newStartDate,  // for reassignment
  unassignEndDate, // for unassign

  // Driver cost automation fields. These are optional so existing flows
  // remain safe. If not configured, old copied/fetched car sheet expenses
  // continue to work as before.
  driverRole = "main", // main | second
  monthlyInstallment = "",
  secondDriverMonthlyExpense = 500,
  costConfigured = false,
  installmentEffectiveFrom = null,
}) {
  if (!driverId) throw new Error("driverId is required");

  const batch = writeBatch(db);

  /* -----------------------------------------------------
        UNASSIGN MODE
  ------------------------------------------------------ */
  if (mode === "unassign") {
    const end = new Date(unassignEndDate);
    end.setHours(0, 0, 0, 0);

    // close active driver assignment
    const q = query(
      collection(db, "driverCarAssignments"),
      where("driverId", "==", driverId),
      where("endDate", "==", null)
    );
    const snap = await getDocs(q);

    let closingMainAssignment = false;

    snap.forEach((d) => {
      const data = d.data() || {};
      const role = String(data.driverRole || "main").toLowerCase();
      if (role !== "second") closingMainAssignment = true;

      batch.update(d.ref, {
        endDate: end,
        updatedAt: serverTimestamp(),
      });
    });

    // clear driver doc
    const driverRef = doc(db, "drivers", driverId);
    batch.update(driverRef, {
      assignedCarId: null,
      assignedCarNumber: null,
      assignedCarType: null,
      assignedCarCompany: null,
      assignedCarStartDate: null,
      updatedAt: serverTimestamp(),
    });

    // clear vehicle doc only when the unassigned record was a main driver.
    // If a second driver is removed, the vehicle's main driver display must
    // remain untouched.
    if (vehicleId && closingMainAssignment) {
      const vehicleRef = doc(db, "vehicles", vehicleId);
      batch.update(vehicleRef, {
        assignedDriverId: null,
        assignedDriverName: null,
        assignedDriverStartDate: null,
        updatedAt: serverTimestamp(),
      });
    }

    await batch.commit();
    return;
  }

  /* -----------------------------------------------------
      ASSIGN OR REASSIGN
  ------------------------------------------------------ */
  const effectiveStartDate = newStartDate || startDate;
  if (!effectiveStartDate)
    throw new Error("startDate or newStartDate is required");

  const start = new Date(effectiveStartDate);
  start.setHours(0, 0, 0, 0);

  const endDateForOld =
    oldEndDate && mode === "reassign"
      ? new Date(oldEndDate)
      : (() => {
          const d = new Date(start);
          d.setDate(d.getDate() - 1);
          return d;
        })();

  /* -----------------------------------------------------
        CLOSE ACTIVE driver assignments
  ------------------------------------------------------ */
  const driverQ = query(
    collection(db, "driverCarAssignments"),
    where("driverId", "==", driverId),
    where("endDate", "==", null)
  );
  const driverSnap = await getDocs(driverQ);

  driverSnap.forEach((docSnap) => {
    batch.update(docSnap.ref, {
      endDate: endDateForOld,
      updatedAt: serverTimestamp(),
    });
  });

  /* -----------------------------------------------------
        CREATE NEW ASSIGNMENT ENTRY
  ------------------------------------------------------ */
  const newAssignRef = doc(collection(db, "driverCarAssignments"));
  const role = String(driverRole || "main").toLowerCase() === "second" ? "second" : "main";
  const monthly = Number(monthlyInstallment || 0);
  const secondMonthly = Number(secondDriverMonthlyExpense || 500);
  const configured = Boolean(costConfigured);

  batch.set(newAssignRef, {
    driverId,
    driverName,
    vehicleId,
    vehicleNumber,
    vehicleType: vehicleType || "",
    company: company || "",
    startDate: start,
    endDate: null,

    // Cost automation fields
    driverRole: role,
    costConfigured: configured,
    installmentMode: configured ? "auto" : "",
    monthlyInstallment: role === "main" && configured ? monthly : 0,
    secondDriverMonthlyExpense: role === "second" && configured ? secondMonthly : 500,
    installmentEffectiveFrom: installmentEffectiveFrom || effectiveStartDate,

    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  /* -----------------------------------------------------
        UPDATE DRIVER DOC
  ------------------------------------------------------ */
  const driverRef = doc(db, "drivers", driverId);
  batch.update(driverRef, {
    assignedCarId: vehicleId,
    assignedCarNumber: vehicleNumber,
    assignedCarType: vehicleType,
    assignedCarCompany: company,
    assignedCarStartDate: start,
    updatedAt: serverTimestamp(),
  });

  /* -----------------------------------------------------
        UPDATE VEHICLE DOC
  ------------------------------------------------------ */
  const vehicleRef = doc(db, "vehicles", vehicleId);

  // Only the main driver should replace the primary assigned driver on
  // the vehicle record. A second driver can be assigned in history without
  // disturbing the vehicle's current main driver display.
  if (role === "main") {
    batch.update(vehicleRef, {
      assignedDriverId: driverId,
      assignedDriverName: driverName,
      assignedDriverStartDate: start,
      updatedAt: serverTimestamp(),
    });
  } else {
    batch.update(vehicleRef, {
      updatedAt: serverTimestamp(),
    });
  }

  await batch.commit();
}
